import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Avatar,
  Badge,
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { apiZodios } from '~api/apiZodios'
import TextField from '~components/common/TextField'
import { Icon } from '~components/internal'
import { type RootState } from '~redux/rootReducer'
import { type CommentThread } from '~types'

import { ASSET_URLS } from '../../constants'

interface CommentComponentProps {
  commentThread: CommentThread
  readOnly?: boolean
  onCommentUpdate?: () => void
  formInReview: boolean
  caseId?: string
}

const CommentComponent: React.FC<CommentComponentProps> = ({
  commentThread,
  readOnly = true,
  onCommentUpdate,
  formInReview,
  caseId,
}) => {
  const [open, setOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [newComment, setNewComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const user = useSelector((state: RootState) => state?.auth?.user)
  const formCaseOwner = useSelector((state: RootState) => state?.formNodes?.formCaseOwner)
  const inputRef = useRef<HTMLInputElement>(null)
  const theme = useTheme()

  const handleToggle = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
    setOpen((prevOpen) => !prevOpen)
    setError(null)
  }

  const handleClose = (): void => {
    setOpen(false)
    setError(null)
  }

  const handleAddComment = async (): Promise<void> => {
    if (!newComment.trim() || !user?.id || !formCaseOwner) return

    try {
      setIsSubmitting(true)
      setError(null)

      await apiZodios.cases_addComment({
        user_id: user.id,
        organization_identity_id: formCaseOwner.id,
        content: newComment,
        author_id: user.identity_id,
        form_id: commentThread.form,
        case_id: caseId,
        input_id: commentThread.input || undefined,
        thread_id: commentThread.id,
      })

      setNewComment('')
      onCommentUpdate?.()
    } catch (err) {
      console.error('Failed to add comment:', err)
      setError('Failed to add comment')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteComment = async (commentId: string): Promise<void> => {
    if (!user?.id || !formCaseOwner?.id) return

    try {
      await apiZodios.cases_deleteComment({
        user_id: user.id,
        organization_identity_id: formCaseOwner.id,
        comment_id: commentId,
      })

      onCommentUpdate?.()
    } catch (err) {
      console.error('Failed to delete comment:', err)
      setError('Failed to delete comment')
    }
  }

  const handleResolveThread = async (): Promise<void> => {
    if (!user?.id) return

    try {
      await apiZodios.cases_resolveThread({
        user_id: user.id,
        thread_id: commentThread.id,
        is_resolved: !commentThread.resolved,
      })

      onCommentUpdate?.()
      handleClose()
    } catch (err) {
      console.error('Failed to resolve thread:', err)
      setError('Failed to resolve thread')
    }
  }

  const commentCount = commentThread?.comments?.length || 0
  if (!commentCount) return null

  return (
    <>
      <IconButton
        size='small'
        onClick={handleToggle}
        sx={{
          color: commentThread.resolved ? theme.palette.grey[500] : theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
          },
          position: 'relative',
        }}
      >
        <Badge
          badgeContent={commentThread.resolved ? 0 : commentCount}
          color={readOnly && formInReview ? 'error' : 'primary'}
        >
          <Icon name='comment' fontSize='medium' />
        </Badge>
      </IconButton>
      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        placement='right-start'
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'left top',
            }}
          >
            <Paper
              elevation={3}
              sx={{
                width: 320,
                maxHeight: 400,
                overflowY: 'auto',
                p: 2,
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {commentThread.comments.map((comment, index) => (
                    <Box key={comment.id} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                          width: '100%',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Avatar
                            src={comment.commenter_profile || ASSET_URLS.icon}
                            alt={comment.commenter_name || 'Polimorphic AI'}
                            sx={{
                              width: 24,
                              height: 24,
                              '& img': {
                                objectFit: 'contain',
                                width: '80%',
                                height: '80%',
                              },
                            }}
                          />
                          <Box>
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight: 'bold',
                              }}
                            >
                              {comment.commenter_name || 'Polimorphic AI'}
                            </Typography>
                            {comment.time && (
                              <Typography
                                variant='caption'
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                {new Date(comment.time).toLocaleString([], {
                                  dateStyle: 'short',
                                  timeStyle: 'short',
                                })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        {index === 0 && !readOnly && (
                          <Tooltip
                            title={commentThread.resolved ? 'Re-open thread' : 'Resolve thread'}
                          >
                            <IconButton sx={{ mb: 3 }} size='small' onClick={handleResolveThread}>
                              {commentThread.resolved ? (
                                <Icon name='replay' fill={0} fontSize='small' />
                              ) : (
                                <Icon name='success' fontSize='small' />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', ml: 4 }}>
                        <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                          {comment.comment}
                        </Typography>
                        {!readOnly && user?.identity_id === comment.commenter && (
                          <IconButton
                            size='small'
                            onClick={() => handleDeleteComment(comment.id)}
                            sx={{ ml: 1, mt: -0.5 }}
                          >
                            <Icon name='delete' fontSize='small' />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  ))}

                  {!readOnly && !commentThread.resolved && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      {error && (
                        <Typography
                          variant='caption'
                          color='error'
                          sx={{ mb: 1, display: 'block' }}
                        >
                          {error}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          inputRef={inputRef}
                          fullWidth
                          size='small'
                          placeholder='Add a comment...'
                          multiline
                          minRows={1}
                          maxRows={3}
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          disabled={isSubmitting}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey) {
                              handleAddComment()
                            }
                          }}
                        />
                        <Tooltip title='Send comment'>
                          <IconButton
                            size='small'
                            onClick={handleAddComment}
                            disabled={!newComment.trim() || isSubmitting}
                          >
                            <Icon name='send' fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default CommentComponent
